import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface DirectReceiptRequest {
  locationCode: string;
  quantity?: number;
  itemBatchNo?: string;
  itemBatchExpDate?: string;
  notes?: string;
  processedBy?: string;
}

export interface DirectReceiptResponse {
  success: boolean;
  errorMessage?: string;
  transaction?: any; // StcTransDto
  processedAt: Date;
  processedBy?: string;
  finalLocation?: string;
}

export interface DirectReceiptStatusResponse {
  isProcessed: boolean;
  processingDate?: Date;
  locationCode?: string;
  processedBy?: string;
  notes?: string;
}

class DirectReceiptService {
  
  /**
   * Prosesserer direkte mottak for en StcTrans oppføring
   */
  async processDirectReceipt(
    batchId: string, 
    lineId: number, 
    request: DirectReceiptRequest
  ): Promise<DirectReceiptResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    try {
      const response = await AuthService.authenticatedPost(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/direct-receipt`,
        request
      );
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.errorMessage || errorData.error || errorData.message || JSON.stringify(errorData);
        } catch (e) {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch (textError) {
            errorMessage = `${errorMessage} ${response.statusText}`;
          }
        }
        throw new Error(`Failed to process direct receipt: ${errorMessage}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Detaljert feil ved direkte mottak:', error);
      console.error('Request data:', request);
      throw error;
    }
  }

  /**
   * Henter status for direkte mottak
   */
  async getDirectReceiptStatus(
    batchId: string, 
    lineId: number
  ): Promise<DirectReceiptStatusResponse> {
    const encodedBatchId = encodeURIComponent(batchId);
    
    try {
      const response = await AuthService.authenticatedGet(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/direct-receipt/status`
      );
      
      if (!response.ok) {
        throw new Error(`Failed to get direct receipt status: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error getting direct receipt status:', error);
      throw error;
    }
  }

  /**
   * Sjekker om direkte mottak er mulig for en StcTrans oppføring
   */
  async canProcessDirectReceipt(
    batchId: string, 
    lineId: number
  ): Promise<{ canProcess: boolean; message: string }> {
    try {
      const status = await this.getDirectReceiptStatus(batchId, lineId);
      
      if (status.isProcessed) {
        return {
          canProcess: false,
          message: 'Direct receipt has already been processed for this line'
        };
      }
      
      return {
        canProcess: true,
        message: 'Direct receipt can be processed'
      };
    } catch (error) {
      console.error('Error checking direct receipt eligibility:', error);
      return {
        canProcess: false,
        message: 'Unable to determine if direct receipt can be processed'
      };
    }
  }
}

export const directReceiptService = new DirectReceiptService();
